import SectionWrapper from '@/components/_rebrand/craft/SectionWrapper';
import { RightArrow } from '@/components/_rebrand/Icons';
import { StyleOverride } from '@/types/craft-style-overrides';
import cn from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { FC } from 'react';

export type ThreeCardLayoutProps = {
  header?: React.ReactNode;
  cards?: {
    image: string;
    title: string;
    description: string;
    comingSoon: boolean;
  }[];
  exploreItems: {
    icon: React.ReactNode,
    title: string;
    link: string;
  }[];
  exploreTitle: React.ReactNode;
  exploreImages: React.ReactNode;
  styleOverrides?: StyleOverride;
}

/**
 * The ThreeCardLayout component is a layout that displays a header, a grid of 6 cards, and an optional "explore" section below. 
 * In Craft, it's called "Feature Grid" or "All Features". It also appears in various hard-coded pages.
 */
const ThreeCardLayout: FC<ThreeCardLayoutProps> = ({
  exploreItems = [],
  cards = [],
  exploreTitle,
  header,
  exploreImages,
  styleOverrides
}) => {

  const hasCards: boolean = (cards.length > 0);

  return (
    <div className="bg-violet-900 text-white">
      <SectionWrapper
        styleOverrides={styleOverrides}
        className={cn(hasCards ? '-mt-header-height-mobile md:-mt-header-height-desktop' : '')}
      >
        <div className={cn(hasCards ? 'pt-header-height-mobile md:pt-header-height-desktop' : '')}>
          <div className="px-container-mobile md:px-container-desktop pb-10">
            {hasCards && (
              <div className="grid gap-2 md:gap-3 text-left mx-auto max-w-grid">
                <div className="flex justify-center">
                  <h2 className="text-white text-43/53 font-500 text-center mb-[64px]">{header}</h2>
                </div>
                <div className="flex flex-wrap justify-center md:justify-between gap-[16px]">
                  {cards.map((card, index) => {
                    return (
                      <div
                        key={`card-${index}`}
                        className={`flex flex-wrap bg-violet-800 rounded-[12px] px-[8px] py-[9px] md:w-[calc(33.333%-16px)]`}
                      >
                        <div className="flex">
                          <Image src={card.image} width={411} height={260} alt={card.title} className="mb-[24px] " />
                        </div>
                        <div className="flex flex-wrap ml-[23px] max-w-[360px]">
                          {card.comingSoon && (
                            <div className="flex uppercase bg-citron-500 p-[4px] rounded-[4px] text-14.5/22 text-blue-900 mb-[12px]">
                              coming soon
                            </div>
                          )}
                          <h5 className="text-white font-500 text-25/33 mb-[12px] w-full">{card.title}</h5>
                          <p className="text-white text-17/25 mb-[32px]">{card.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {(exploreItems.length > 0) ? (
              <div
                className={cn('flex justify-center w-full',
                  hasCards ? 'pt-header-height-mobile md:pt-header-height-desktop' : 'pt-0 mt-0',
                  'flex-wrap items-center mx-auto max-w-grid')}
              >
                <div className="flex w-full mb-4">
                  <h2 className="text-white text-43/52 font-500 w-full md:pb-[10px]">{exploreTitle}</h2>
                </div>
                <div className="flex w-full md:w-1/2 flex-wrap items-center mt-[32px] md:pr-[24px]">

                  {exploreItems.map((item, index) => {
                    return (
                      <Link href={item.link} key={`explore-${index}`} className="w-full">
                        <div
                          className="flex flex-wrap w-full max-w-[500px] border-b-[1px] border-violet-800 border-solid relative transition-all
                before:w-0 before:h-[1px] before:absolute before:bottom-0 before:right-0 before:bg-violet-800 before:transition-all before:duration-500
                hover:before:w-full hover:before:left-0 hover:before:bg-citron-500 hover:cursor-pointer
              "
                        >
                          <div className="flex w-full pt-[16px] pb-[18px]">
                            <div className="flex rounded-[50%] w-[32px] h-[32px] bg-citron-500 p-[8px] mr-[18px]">
                              {item.icon}
                            </div>
                            <div className="flex w-full justify-between">
                              <div className="flex text-white text-21/29">{item.title}</div>
                              <div className="flex"><RightArrow arrowStroke='#fff' /></div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
                <div className="flex w-full md:w-1/2 flex-wrap mt-[32px] md:mt-0">{exploreImages}</div>
              </div>) : null}
          </div>
        </div>
      </SectionWrapper>
    </div>
  );
};

export default ThreeCardLayout;
